/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { RootStore } from 'src/core/stores/RootStore';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { AutoLabelingInfoDto, AutoLabelingSteps } from 'src/customText/verticals/autotagTriggerModal/types';

export const contentSizeToTextRecords = (sizeInBytes: number, language: string) =>
    Math.ceil(
        sizeInBytes /
            (LuisConstants.CustomText.LanguageMaxCharacterSizeInBytes.find(lan => lan.cultureCode === language)?.bytes ??
                LuisConstants.CustomText.DefaultLanguageMaxCharacterSizeInBytes) /
            1000
    );

export const getAutoLabelingStepStatus = (
    stepIndex: AutoLabelingSteps,
    activeStep: number,
    autoLabelingInfoDto: AutoLabelingInfoDto,
    rootStore: RootStore
): { state: 'none' | 'completed' | 'active'; isValid: boolean } => {
    const isGptJob = autoLabelingInfoDto.autoLabelingJobType === 'gpt';
    const { ownedAzureOpenAiResourceId } = rootStore.azureStore.session.csAccountStore;
    const selectedTextRecords = autoLabelingInfoDto.documents.reduce(
        (val, acc) => contentSizeToTextRecords(acc.contentSize, acc.language) + val,
        0
    );
    const { autoTaggingQuota: projectAutoTaggingQuota, projectKind } = rootStore.currentCustomTextProjectStore;
    const autoTaggingQuota = projectAutoTaggingQuota || { consumedQuota: 0, totalQuota: selectedTextRecords };
    const isAutoLabelingComplete = Boolean(autoLabelingInfoDto.autoLabelingJobType);
    const isConfigureJobComplete =
        isAutoLabelingComplete &&
        ((isGptJob && Boolean(ownedAzureOpenAiResourceId) && Boolean(autoLabelingInfoDto.azureOpenAiDeploymentName)) ||
            (autoLabelingInfoDto.autoLabelingJobType === 'trainedModel' && Boolean(autoLabelingInfoDto.trainedModelLabel)));
    const isSelectEntitiesComplete =
        isConfigureJobComplete && projectKind === 'CustomEntityRecognition' && Boolean(autoLabelingInfoDto.entitiesNames.length);
    const isSelectClassesComplete =
        isConfigureJobComplete && projectKind !== 'CustomEntityRecognition' && Boolean(autoLabelingInfoDto.classesNames.length);
    const isSelectEntitiesOrClassesComplete = isSelectEntitiesComplete || isSelectClassesComplete;
    const isSelectFilesComplete =
        isSelectEntitiesOrClassesComplete &&
        autoLabelingInfoDto.documents.length &&
        (isGptJob || selectedTextRecords <= autoTaggingQuota.totalQuota - autoTaggingQuota.consumedQuota);
    const stepsValidity: boolean[] = [
        isAutoLabelingComplete,
        isConfigureJobComplete,
        isSelectEntitiesOrClassesComplete,
        isSelectFilesComplete,
        isSelectFilesComplete
    ];

    if (stepIndex === AutoLabelingSteps.AutoLabelingJob && activeStep === AutoLabelingSteps.ConfigureJob) {
        return { state: 'active', isValid: isConfigureJobComplete };
    }

    if (stepIndex === activeStep) {
        return { state: 'active', isValid: stepsValidity[stepIndex] };
    }

    switch (stepIndex) {
        case AutoLabelingSteps.AutoLabelingJob:
            return { state: isAutoLabelingComplete ? 'completed' : 'none', isValid: stepsValidity[stepIndex] };
        case AutoLabelingSteps.ConfigureJob:
            return { state: isConfigureJobComplete ? 'completed' : 'none', isValid: stepsValidity[stepIndex] };
        case AutoLabelingSteps.SelectEntitiesOrClasses:
            return { state: isSelectEntitiesOrClassesComplete ? 'completed' : 'none', isValid: stepsValidity[stepIndex] };
        case AutoLabelingSteps.SelectFiles:
            return { state: isSelectFilesComplete ? 'completed' : 'none', isValid: stepsValidity[stepIndex] };
        case AutoLabelingSteps.ReviewAndFinish:
            return { state: isSelectFilesComplete ? 'completed' : 'none', isValid: stepsValidity[stepIndex] };
        default:
            return { state: 'completed', isValid: true };
    }
};
